import equal from "fast-deep-equal";
import { useMemo, useRef } from "react";

/**
 * Swaps each row of `next` for the row `previous` holds under the same key when the two are deeply equal, and returns
 * `previousRows` itself when every row was kept in the same order.
 *
 * The dispatch computes rebuild every driver's object on every run, and the table's row memo skips a row only when it
 * receives the same object it rendered last time. Keeping unchanged rows' identity is what lets a write that touches
 * one driver re-render one row, and returning the old array lets every memo keyed on it skip the whole pass.
 */
export function keepEqualRows<T>(
  previousRows: T[],
  previous: ReadonlyMap<string, T>,
  next: readonly T[],
  keyOf: (row: T) => string,
): { rows: T[]; byKey: Map<string, T> } {
  const byKey = new Map<string, T>();
  let unchanged = next.length === previousRows.length;
  const rows = next.map((row, index) => {
    const key = keyOf(row);
    const prior = previous.get(key);
    const kept = prior !== undefined && (prior === row || equal(prior, row)) ? prior : row;
    byKey.set(key, kept);
    if (kept !== previousRows[index]) unchanged = false;
    return kept;
  });
  return { rows: unchanged ? previousRows : rows, byKey };
}

/**
 * {@link keepEqualRows} across renders: the rows `next` holds, with every row equal to the one last returned under
 * its key kept as that same object.
 */
export function useStableRows<T>(next: readonly T[], keyOf: (row: T) => string): T[] {
  const previousRef = useRef<{ rows: T[]; byKey: ReadonlyMap<string, T> }>({ rows: [], byKey: new Map() });
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyOf names the key field; callers pass a stable function
  return useMemo(() => {
    const kept = keepEqualRows(previousRef.current.rows, previousRef.current.byKey, next, keyOf);
    previousRef.current = kept;
    return kept.rows;
  }, [next]);
}
